import {
  assignEmployeeToExternalShift,
  removeAssignedEmployeeFromExternalShift,
  updateShiftEmployee,
  NO_EMPLOYEE,
  shiftKeys,
  shiftsWithContentsQueryOptions,
} from "@/api/Shifts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import styles from "./ShiftDetails.module.scss";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import Select from "@medoma/components/Select/Select";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useEffect, useState } from "react";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import {
  employeeName,
  type IEmployee,
  type IScheduledShift,
} from "@medoma/models/shifts";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import { useUnassignActivitiesAndVisitsFromShift } from "./useUnassignActivitiesAndVisitsFromShift";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import { Text } from "@medoma/components/Text/Text";
import { getPatientFullName } from "@medoma/models/patients";
import { calculateVisitTimes } from "@medoma/models/visits";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import {
  detectKnownShiftOverlapError,
  SHIFT_OVERLAP_ERROR,
} from "./detectKnownShiftOverlapError";

const MOCK_UPDATING_SCHEDULE_DURATION = 7000;
const MOCK_FETCHING_NEW_SCHEDULE_DURATION = 3000;

const useShiftWithContents = (shift: IScheduledShift) => {
  const {
    data: shiftsWithContents,
    isPending,
    isError,
  } = useQuery({
    ...shiftsWithContentsQueryOptions({
      selectedDate: new Date(shift.startDateTime),
    }),
    enabled: true,
  });

  // Find the specific shift among the shifts with contents
  const shiftWithContents = shiftsWithContents
    ? (shiftsWithContents.relevant.find(
        (shiftWithContent) => shiftWithContent.shift.id === shift.id,
      ) ??
      shiftsWithContents.before.find(
        (shiftWithContent) => shiftWithContent.shift.id === shift.id,
      ) ??
      shiftsWithContents.after.find(
        (shiftWithContent) => shiftWithContent.shift.id === shift.id,
      ))
    : undefined;

  return {
    shiftWithContents,
    isPending,
    isError,
  };
};

export const ChangeEmployee = ({
  shift,
  employees,
  isQuinyxShift,
}: {
  shift: IScheduledShift;
  employees: IEmployee[];
  isQuinyxShift: boolean;
}) => {
  const { t } = useLingui();
  const { shiftWithContents } = useShiftWithContents(shift);
  const { confirm } = useDialog();
  const shiftId = shift.id;

  const {
    register,
    unregister,
    handleSubmit,
    watch,
    reset: resetForm,
    formState: { errors },
  } = useForm<{
    scheduledEmployeeId: IEmployee["id"];
    onUnassignShift:
      | "clear-activities-and-visits"
      | "keep-activities-and-visits";
  }>({
    defaultValues: {
      scheduledEmployeeId: shift.employee?.id ?? NO_EMPLOYEE,
    },
  });

  const [isChangingEmployee, setIsChangingEmployee] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutate: mutateEmployee, isPending: isMutatingEmployee } = useMutation(
    {
      mutationFn: ({
        employeeId,
        shiftId,
      }: Parameters<typeof updateShiftEmployee>[0]) => {
        if (employeeId === NO_EMPLOYEE) {
          return updateShiftEmployee({ shiftId, employeeId: null });
        }
        return updateShiftEmployee({ employeeId, shiftId });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
        setLoadingMessage(null);
        setIsChangingEmployee(false);
      },
      onError: (error) => {
        setLoadingMessage(null);
        displayErrorMessageAlert(
          `${t`Det gick inte att genomföra ändringen`}. ${deducedError(error)}`,
        );
      },
    },
  );

  const {
    mutate: mutateEmployeeExternalShift,
    isPending: isMutatingEmployeeExternalShift,
  } = useMutation({
    mutationFn: ({
      employeeId,
      shiftId,
    }: Parameters<typeof assignEmployeeToExternalShift>[0]) => {
      if (employeeId === NO_EMPLOYEE) {
        return removeAssignedEmployeeFromExternalShift({ shiftId });
      }
      return assignEmployeeToExternalShift({ employeeId, shiftId });
    },
    onSuccess: async () => {
      setLoadingMessage(t`Uppdaterar schema`);
      setTimeout(() => {
        setLoadingMessage(t`Hämtar nytt schema`);
        // Keep the user waiting for a while for the Quinyx scheduling system to update and new data to be fetched
        // Most of the time, 10 seconds will do it. Sometimes, the data will not have been updated by then,
        // but this is a rare case and the user can just refresh the page if they need to.
        setTimeout(async () => {
          await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
          setLoadingMessage(null);
          setIsChangingEmployee(false);
        }, MOCK_FETCHING_NEW_SCHEDULE_DURATION);
      }, MOCK_UPDATING_SCHEDULE_DURATION);
    },
    onError: (error) => {
      setLoadingMessage(null);
      const errorType = detectKnownShiftOverlapError(error);
      displayErrorMessageAlert(
        `${t`Det gick inte att genomföra ändringen`}. ${
          errorType === SHIFT_OVERLAP_ERROR
            ? t`Medarbetaren är redan tilldelad ett annat pass som överlappar med detta` +
              "."
            : deducedError(error)
        }`,
      );
    },
  });

  const {
    mutateAsync: unassignActivitiesAndVisitsFromShift,
    isPending: isUnassigningActivitiesAndVisitsFromShift,
  } = useUnassignActivitiesAndVisitsFromShift();

  const isAboutToSetShiftToUnassigned =
    // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
    Number(watch("scheduledEmployeeId")) === NO_EMPLOYEE;
  const shiftIsCurrentlyAssigned = shift.employee !== null;
  const showUnassignShiftOptions =
    isAboutToSetShiftToUnassigned && shiftIsCurrentlyAssigned;
  const disableButtons =
    isMutatingEmployee ||
    isMutatingEmployeeExternalShift ||
    isUnassigningActivitiesAndVisitsFromShift ||
    loadingMessage !== null;
  const onUnassignShiftOptions = {
    required: {
      value: true,
      message: t`Du måste välja hur du vill hantera oavslutade aktiviteter och besök på detta arbetspass när du tar bort medarbetaren`,
    },
  };

  // Unregister clear-activities-and-visits option if options are not shown
  // This is to prevent the user from selecting a value, and then changing the employee selection to an employee
  // that is not unassigned, and then submitting the form with a hidden value that has not been cleared
  useEffect(() => {
    if (!showUnassignShiftOptions) {
      unregister("onUnassignShift");
    }
  }, [showUnassignShiftOptions, unregister]);

  return (
    <>
      <div className={styles.actions}>
        {isChangingEmployee ? (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingEmployee(false);
            }}
            disabled={disableButtons}
            aria-label={t`Avbryt ändring av medarbetare`}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingEmployee(true);
            }}
            disabled={disableButtons}
            aria-label={
              shift.employee ? t`Ändra medarbetare` : t`Lägg till medarbetare`
            }
          >
            {shift.employee ? <Trans>Ändra</Trans> : <Trans>Lägg till</Trans>}
          </PlainButton>
        )}
      </div>
      {isChangingEmployee ? (
        <div className={styles.editContainer}>
          <Form
            onSubmit={handleSubmit(async (validatedFormData) => {
              const shouldClearActivitiesAndVisits =
                validatedFormData.onUnassignShift ===
                "clear-activities-and-visits";
              const informationAboutVisitsAndActivitiesAvailable =
                shiftWithContents !== undefined;

              // If the user wants to clear activities and visits,
              // and there is no information about visits and activities available,
              // we need to confirm with the user that they want to continue
              if (
                shouldClearActivitiesAndVisits &&
                !informationAboutVisitsAndActivitiesAvailable
              ) {
                const confirmContinueUnassign = await confirm({
                  title: t`Kunde inte hämta information om besök och aktiviteter`,
                  message: t`Det gick inte se om det finns besök och aktiviteter tilldelade detta arbetspass. Vill du fortsätta ändå?`,
                });
                if (!confirmContinueUnassign) {
                  return;
                }
              }

              // If the user wants to clear activities and visits,
              // and is about to set the shift to unassigned,
              // and there is information about visits and activities available,
              // we can proceed with unassigning activities and visits
              if (
                shouldClearActivitiesAndVisits &&
                isAboutToSetShiftToUnassigned &&
                informationAboutVisitsAndActivitiesAvailable
              ) {
                const { failedOperations, totalOperations, failures } =
                  await unassignActivitiesAndVisitsFromShift(shiftWithContents);

                // Handle user experience if there were any failures
                if (failedOperations > 0) {
                  const ONE_LINE_BREAK = "\n";
                  const TWO_LINE_BREAKS = "\n\n";
                  const failedVisitsAndActivityOccurrences = failures
                    .map((failure) => {
                      // If the failure is a visit, return the patient name, time of visit, and a list of occurrences in the visit
                      if (failure.visit) {
                        const { patient } = failure.visit;
                        const patientName =
                          "name" in patient
                            ? getPatientFullName(patient.name)
                            : undefined;
                        const timespan = calculateVisitTimes(failure.visit);
                        const timespanString = timespan.isAnyTimeOfDay
                          ? t(
                              timeOfDayDictionary[timeOfDaySchema.enum.Any]
                                .short,
                            )
                          : timespan.start
                            ? formattedTimeSpan(timespan.start, timespan.end)
                            : undefined;
                        const listOfOccurrences = failure.visit.occurrences
                          .map((occurrence) => {
                            return occurrence.title;
                          })
                          .join(", ");
                        const visitDetails = [
                          patientName,
                          timespanString,
                          listOfOccurrences,
                        ]
                          .filter(Boolean)
                          .join(", ");
                        return visitDetails;

                        // If the failure is an activity occurrence, return the patient name, time of occurrence, and the activity title
                      } else if (failure.activityOccurrence) {
                        const { activityOccurrence } = failure;
                        const { patient, title, start, end, timeOfDay } =
                          activityOccurrence;
                        const patientName =
                          patient && "name" in patient
                            ? getPatientFullName(patient.name)
                            : undefined;
                        const timeOfOccurrence =
                          timeOfDay === timeOfDaySchema.enum.Any
                            ? t(
                                timeOfDayDictionary[timeOfDaySchema.enum.Any]
                                  .short,
                              )
                            : formattedTimeSpan(start, end);
                        const activityOccurrenceDetails = [
                          patientName,
                          title,
                          timeOfOccurrence,
                        ]
                          .filter(Boolean)
                          .join(", ");
                        return activityOccurrenceDetails;
                      }
                    })
                    .join(`${ONE_LINE_BREAK}`);

                  // If there are errors from unassigning activities and visits,
                  // inform the user about which activities and visits that were not unassigned and left on the shift,
                  // and ask the user if they want to continue unassigning the shift anyway
                  if (failedOperations > 0) {
                    const confirmContinueUnassign = await confirm({
                      title: t`Vissa besök och aktiviteter kunde inte tas bort`,
                      message: t`${failedOperations} av ${totalOperations} aktiviteter och besök kunde inte tas bort från arbetspasset: ${TWO_LINE_BREAKS}${failedVisitsAndActivityOccurrences}${TWO_LINE_BREAKS}Vill du fortsätta att ta bort medarbetaren från arbetspasset även om vissa besök och aktiviteter inte kunde tas bort? `,
                    });
                    if (!confirmContinueUnassign) {
                      resetForm();
                      return;
                    }
                  }
                }
              }
              setLoadingMessage(
                validatedFormData.scheduledEmployeeId === NO_EMPLOYEE
                  ? t`Tar bort medarbetare`
                  : t`Byter medarbetare`,
              );
              if (isQuinyxShift) {
                mutateEmployeeExternalShift({
                  employeeId: validatedFormData.scheduledEmployeeId,
                  shiftId,
                });
              } else {
                mutateEmployee({
                  employeeId: validatedFormData.scheduledEmployeeId,
                  shiftId,
                });
              }
            })}
          >
            {isUnassigningActivitiesAndVisitsFromShift ? (
              <Loading message={t`Tar bort aktiviteter och besök`} />
            ) : loadingMessage ? (
              <Loading message={loadingMessage} />
            ) : (
              <>
                <Select
                  label={t`Välj medarbetare`}
                  {...register("scheduledEmployeeId", { valueAsNumber: true })}
                >
                  <option value={NO_EMPLOYEE}>
                    <Trans>Ingen</Trans>
                  </option>
                  {employees.map((employee) => {
                    return (
                      <option value={employee.id} key={employee.id}>
                        {employeeName(employee, { employeeNameFormat: "full" })}
                        {employee.distinctionLabel
                          ? ` (${employee.distinctionLabel})`
                          : null}
                      </option>
                    );
                  })}
                </Select>
                {showUnassignShiftOptions ? (
                  <RadioButtons
                    legend={t`Hur vill du hantera oavslutade aktiviteter och besök på detta arbetspass?`}
                    errorMessage={errors.onUnassignShift?.message}
                  >
                    <RadioButton
                      {...register("onUnassignShift", onUnassignShiftOptions)}
                      value="clear-activities-and-visits"
                      label={{
                        text: t`Flytta tillbaka till oplanerat`,
                      }}
                    />
                    <RadioButton
                      {...register("onUnassignShift", onUnassignShiftOptions)}
                      value="keep-activities-and-visits"
                      label={{
                        text: t`Behåll på arbetspasset`,
                      }}
                    />
                    <Text
                      element="p"
                      color="faded"
                    >{t`Aktiviteter och besök som även är tilldelade andra arbetspass påverkas inte — de ligger kvar.`}</Text>
                  </RadioButtons>
                ) : null}

                <FilledButton disabled={disableButtons} type="submit">
                  <Trans>Spara ändringar</Trans>
                </FilledButton>
              </>
            )}
          </Form>
        </div>
      ) : null}
    </>
  );
};
